import _ from 'lodash'

import UserManager from '@/user-manager'

import Renovation from '@/models/renovation'
import Guard from 'arg-guard'

/**
 * Lookup of friendly translated plan instances,
 * @type {Array<{ position: number, textFragment: string }>}
 */
const friendlyRenovationLookup = [
    { position: 1, textFragment: '' },
    { position: 2, textFragment: 'Second' },
    { position: 3, textFragment: 'Third' },
    { position: 4, textFragment: 'Fourth' },
    { position: 5, textFragment: 'Fifth' },
    { position: 6, textFragment: 'Sixth' },
    { position: 7, textFragment: 'Seventh' },
    { position: 8, textFragment: 'Eighth' },
    { position: 9, textFragment: 'Ninth' },
    { position: 10, textFragment: 'Tenth' },
]

/**
 * Tool for determining default renovation names.
 * @export RenovationNameGenerator
 */
const RenovationNameGenerator = {

    /**
     * Loads existing renovation plans and uses these to determine the name of the next plan in the list.
     * @param {number} addressId
     * @returns {Promise<string>} The next friendly name.
     */
    async generateNextAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        const profile = await UserManager.loadProfileDataAsync()
        const forAddress = profile.addresses
            .find(addr => addr.id === addressId)

        if (forAddress == null)
            throw new Error('The addressId provided was not linked to the current user profile')

        return this.generateNextForRenovations(forAddress.renovations)
    },

    /**
     * Determines the name of the next plan in the list of a provided set of renovation plans.
     * @param {Array<Renovation>} existingRenovations A list of plans to look for the next name for.
     * @returns {string} The next friendly name.
     */
    generateNextForRenovations(existingRenovations) {
        if (existingRenovations.length === 0)
            return Renovation.DEFAULT_NAME

        const lastRenovation = existingRenovations[existingRenovations.length - 1]
        const lastPlanPosition = this._getPositionNumber(lastRenovation.name)
        const nextPlanPosition = lastPlanPosition + 1

        return nextPlanPosition <= friendlyRenovationLookup[friendlyRenovationLookup.length - 1].position
            ? Renovation.templatedFriendlyName(friendlyRenovationLookup
                .find(lkp => lkp.position === nextPlanPosition).textFragment)
            : Renovation.templatedBasicName(nextPlanPosition)
    },

    /**
     * @param {string} renovationName
     * @returns {number} The integer representing the last created plan in the set.
     * @private
     */
    _getPositionNumber(renovationName) {
        const prefixRemoved = _.replace(renovationName, /My /g, '')
        const clean = _.replace(prefixRemoved, /Renovation/g, '').trim()

        const retVal = parseInt(clean)

        return isNaN(retVal)
            ? friendlyRenovationLookup
                .find(lkp => lkp.textFragment === clean).position
            : retVal
    },
}

export default RenovationNameGenerator
